/**
 * Utility functions to help manage detachable components
 * These helpers provide a way to control detachable components
 * without directly importing all their contexts
 */

/**
 * Closes all detachable components by directly updating localStorage
 * and dispatching a custom event to trigger updates
 */
export const closeAllDetachableComponents = () => {
  // Close Clock
  localStorage.setItem('persistentClockVisible', 'false');
  localStorage.setItem('persistentClockDetached', 'false');
  
  // Close Calculator
  localStorage.setItem('persistentCalculatorVisible', 'false');
  localStorage.setItem('persistentCalculatorDetached', 'false');
  
  // Close Calendar
  localStorage.setItem('calendarDetached', 'false');
  
  // Dispatch a custom event to notify components of the change
  // This ensures components react to localStorage changes immediately
  window.dispatchEvent(new Event('storage'));
};

/**
 * Ensures detachable component positions are within viewport bounds
 * This prevents components from being positioned off-screen when window is resized
 * @param {string} positionKey - localStorage key for the component position
 * @param {Object} defaultPosition - Default position to use if needed
 * @param {number} componentWidth - Width of the component in pixels
 * @param {number} componentHeight - Height of the component in pixels
 * @param {boolean} maintainRightDistance - Whether to maintain distance from right edge when resizing
 * @returns {Object} The adjusted position coordinates
 */
export const ensureComponentInViewport = (
  positionKey, 
  defaultPosition = { x: window.innerWidth - 350, y: 80 },
  componentWidth = 280,
  componentHeight = 340,
  maintainRightDistance = false
) => {
  // Try to load saved position from localStorage
  const savedPosition = localStorage.getItem(positionKey);
  let position = defaultPosition;
  
  if (savedPosition) {
    try {
      position = JSON.parse(savedPosition);
    } catch (e) {
      console.error(`Error parsing saved position for ${positionKey}:`, e);
    }
  }
  
  // Get current viewport dimensions
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Calculate bounds to ensure the component stays in viewport
  // Leave some margin for better visibility
  const margin = 20;
  const minX = margin;
  const maxX = viewportWidth - componentWidth - margin;
  const minY = margin;
  const maxY = viewportHeight - componentHeight - margin;
  
  // When maintainRightDistance is true, check if we have a stored right distance
  // If not, calculate and store the right distance from the current position
  const rightDistanceKey = `${positionKey}_rightDistance`;
  let rightDistance = localStorage.getItem(rightDistanceKey);
  
  if (maintainRightDistance) {
    if (!rightDistance) {
      // Calculate distance from right edge based on current position
      rightDistance = viewportWidth - position.x - componentWidth;
      // Store it for future reference
      localStorage.setItem(rightDistanceKey, rightDistance);
    } else {
      // Parse the stored right distance
      rightDistance = parseInt(rightDistance, 10);
      
      // Calculate new X position based on right distance
      position.x = viewportWidth - componentWidth - rightDistance;
    }
  }
  
  // Adjust position if needed to stay within bounds
  const adjustedPosition = {
    x: Math.max(minX, Math.min(maxX, position.x)),
    y: Math.max(minY, Math.min(maxY, position.y))
  };
  
  // If position was adjusted, save the new position to localStorage
  if (adjustedPosition.x !== position.x || adjustedPosition.y !== position.y) {
    localStorage.setItem(positionKey, JSON.stringify(adjustedPosition));
    
    // If we adjusted X position and maintaining right distance, update the stored right distance
    if (maintainRightDistance && adjustedPosition.x !== position.x) {
      rightDistance = viewportWidth - adjustedPosition.x - componentWidth;
      localStorage.setItem(rightDistanceKey, rightDistance);
    }
  }
  
  return adjustedPosition;
};

/**
 * Handle window resize events to keep detached components in viewport
 * This function should be called once to set up the resize handler
 */
export const setupDetachableComponentsResizeHandler = () => {
  // Store the previous window width to detect resize direction
  let prevWidth = window.innerWidth;
  
  const handleResize = () => {
    const currentWidth = window.innerWidth;
    // Determine if the screen is growing or shrinking
    const isGrowing = currentWidth > prevWidth;
    
    // Adjust Clock position
    const clockPosition = ensureComponentInViewport(
      'expenseClockPosition', 
      { x: window.innerWidth - 280, y: 80 },
      280, 
      350, // Updated compact height for timer functionality
      isGrowing // Maintain right distance when screen is growing
    );
    
    // Adjust Calculator position
    const calculatorPosition = ensureComponentInViewport(
      'expenseCalculatorPosition', 
      { x: window.innerWidth - 350, y: 80 },
      280, 
      400,
      isGrowing
    );
    
    // Adjust Calendar position
    const calendarPosition = ensureComponentInViewport(
      'calendarPosition', 
      { x: window.innerWidth - 380, y: 80 },
      320, 
      400,
      isGrowing
    );
    
    // Update previous width for next resize event
    prevWidth = currentWidth;

    // Notify components of the changes
    window.dispatchEvent(new Event('storage'));
  };

  // Debounced: 'resize' fires continuously (often on every animation frame)
  // while a window is actively being dragged to a new size, and handleResize
  // does up to 6 synchronous localStorage reads/writes (2 per widget, via
  // ensureComponentInViewport) plus a window.dispatchEvent(new
  // Event('storage')) - which 7 other places in the app (Dashboard,
  // LandingPage, TrialStatus, Settings, Login, WeatherThemedLayout, App.js
  // itself) listen for and react to, several by re-rendering or refetching.
  // Undebounced, an ordinary resize drag fired all of that dozens of times a
  // second. The widgets only need to end up correctly positioned once the
  // window settles - not repositioned live on every intermediate frame - so
  // waiting for a short pause in resize activity preserves the same end
  // result while collapsing that whole burst down to one run.
  let resizeTimeoutId = null;
  const debouncedHandleResize = () => {
    if (resizeTimeoutId !== null) {
      clearTimeout(resizeTimeoutId);
    }
    resizeTimeoutId = setTimeout(() => {
      resizeTimeoutId = null;
      handleResize();
    }, 200);
  };

  // Set up resize handler
  window.addEventListener('resize', debouncedHandleResize);

  // Run initially (not debounced) to fix any already out-of-bounds components
  handleResize();

  // Return function to clean up event listener if needed
  return () => {
    window.removeEventListener('resize', debouncedHandleResize);
    if (resizeTimeoutId !== null) {
      clearTimeout(resizeTimeoutId);
    }
  };
}; 